import { ValidationResult } from 'joi';

import { ValidationFunctionResult } from '../../../../shared/types/validation-function-result';
import { setStatusSchema } from '../../../../shared/validators/schemas/set-status.schema';
import { VehicleInsuranceRecordDto } from '../../dtos/vehicle-insurance-record.dto';
import { VehicleInsuranceActionEnum } from '../../enums/vehicle-insurance-action.enum';
import { VehicleInsuranceStatusEnum } from '../../enums/vehicle-insurance-status.enum';

/**
 * Validates if an action can be performed by validating the record data.
 * @param record - The record for the data validation.
 * @param action - The action to be validated.
 * @param abortEarly - Whether to abort the validation on the first error.
 * @returns The validation result.
 */
export const canPerformAction = (
	record: VehicleInsuranceRecordDto,
	action: VehicleInsuranceActionEnum,
	abortEarly: boolean = false,
): ValidationFunctionResult => {
	// Check if record is provided
	if (!record) throw new Error('record is required.');

	let validationResult: ValidationResult | undefined;

	const fixedStatuses = [
		VehicleInsuranceStatusEnum.PENDING,
		VehicleInsuranceStatusEnum.EXPIRED,
		VehicleInsuranceStatusEnum.ACTIVE,
	];

	switch (action) {
		case VehicleInsuranceActionEnum.ACTIVATE:
			validationResult = setStatusSchema(
				VehicleInsuranceStatusEnum.ACTIVE,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		case VehicleInsuranceActionEnum.PENDING:
			validationResult = setStatusSchema(
				VehicleInsuranceStatusEnum.PENDING,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		case VehicleInsuranceActionEnum.EXPIRE:
			validationResult = setStatusSchema(
				VehicleInsuranceStatusEnum.EXPIRED,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;
		default:
			throw new Error(`Unknown action: ${action}`);
	}

	// Check for validation errors
	if (validationResult?.error) {
		return {
			isValid: false,
			errors: validationResult.error.details?.map((e) => e.message),
		};
	}

	// Return valid result if no errors
	return { isValid: true };
};
